/**
 * steps/BrokerSelectionStep.tsx
 * Broker selection step for the onboarding wizard
 * 
 * Clean wizard step implementation using modernized broker components.
 * Integrates with new WizardDataProvider context system and follows
 * established wizard step patterns.
 */

// 1. External dependencies
import React, { useEffect, useMemo } from 'react';
import { Building2, TrendingUp, Info } from 'lucide-react';

// 2. Internal absolute imports
import { Card, CardBody, CardHeader, CardFooter, Alert, Accordion, AccordionItem } from '@enfomentis/ui';
import { BrokerSelectionForm, validateBrokerSelection, validateAccountSetupPrerequisites, type BrokerSelectionAccountData } from '@/components/shared/Brokers';
import { useBrokerSelection } from '@/hooks/useBrokerSelection';
import { useBrokerFeeDetails } from '@/hooks/useBrokerFeeDetails';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';

// Asset class conversion logic is handled by BrokerSelectionForm

/**
 * Broker selection step component
 */
export const BrokerSelectionStep: React.FC = () => {
  const { draft, updateBroker, clearBrokerSelection } = useOnboardingData();

  // Prepare account data for broker selection
  const accountData: BrokerSelectionAccountData = useMemo(() => ({
    accountType: draft?.account?.accountType || 'personal',
    selectedAssetClasses: draft?.account?.selectedAssetClasses || [],
    ...(draft?.account?.propFirmDetails?.firmName && {
      propFirmId: draft.account.propFirmDetails.firmName
    }),
    ...(draft?.account?.propFirmDetails?.accountStage && {
      accountStage: draft.account.propFirmDetails.accountStage
    }),
    selectedBrokerId: draft?.account?.selectedBrokerId || '',
    customAccountTypeName: draft?.account?.customAccountTypeName || ''
  }), [
    draft?.account?.accountType,
    draft?.account?.selectedAssetClasses,
    draft?.account?.propFirmDetails?.firmName,
    draft?.account?.propFirmDetails?.accountStage,
    draft?.account?.selectedBrokerId,
    draft?.account?.customAccountTypeName
  ]);

  // Get all brokers for validation
  const { all: allBrokersUnfiltered } = useBrokerSelection({
    ...(accountData.accountType && { accountType: accountData.accountType }),
    showAllBrokers: true
  });

  // Pre-fetch broker fees for selected broker to eliminate loading flash on next step
  // This runs silently in the background and caches the data
  useBrokerFeeDetails(accountData.selectedBrokerId || null);

  // Validate saved broker selection against all available brokers
  useEffect(() => {
    const savedBrokerId = accountData.selectedBrokerId;
    if (savedBrokerId && allBrokersUnfiltered.length > 0) {
      const isSelectionValid = allBrokersUnfiltered.some(broker => broker.id === savedBrokerId) ||
                              savedBrokerId === 'custom-broker';

      if (!isSelectionValid) {
        clearBrokerSelection();
      }
    }
  }, [accountData.selectedBrokerId, allBrokersUnfiltered, clearBrokerSelection]);

  /**
   * Handle broker selection
   */
  const handleBrokerSelect = async (brokerId: string): Promise<void> => {
    try {
      await updateBroker({
        selectedBrokerId: brokerId,
        brokerSnapshot: {
          brokerId,
          brokerName: '', // Will be filled by the service
          context: 'onboarding'
        }
      });
    } catch (error) {
      console.error('Failed to update broker selection:', error);
    }
  };


  /**
   * Check if form is valid
   */
  const isFormValid = (): boolean => {
    const validation = validateBrokerSelection(accountData);
    return validation.isValid;
  };

  // Check for missing prerequisites
  const shouldShowErrorState = useMemo(() => {
    if (!draft?.account) return false;
    const validation = validateAccountSetupPrerequisites(accountData);
    return !validation.isValid;
  }, [draft?.account, accountData]);

  // Error state for missing account prerequisites
  if (shouldShowErrorState) {
    return (
      <Card
        radius="none"
        shadow="none"
        className="h-full max-h-dvh w-full flex flex-col border-0">
        <CardBody className="space-y-6">
          <div className="max-w-2xl mx-auto px-2">
            <Alert
              color="danger"
              variant="bordered"
              title="Missing Account Information"
              description="Please complete the account setup first to select a broker."
              startContent={<Building2 className="w-5 h-5" />}
              className="text-center"
            />
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-dvh w-full flex flex-col border-0">

      <CardHeader>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          <span className="text-large font-semibold">Select Your Broker</span>
        </div>
      </CardHeader>

      <CardBody className="space-y-6">
        <div className="max-w-2xl mx-auto px-2">
          {/* Broker Selection Form */}
          <div className="space-y-6">
            <div>
              <p className="text-sm text-default-600 mb-6">
                Choose your trading broker based on your account type and asset preferences.
              </p>
            </div>

            <BrokerSelectionForm
              accountData={accountData}
              onBrokerSelect={handleBrokerSelect}
              searchPlaceholder={accountData.accountType === 'personal'
                ? 'Search brokers for personal accounts...'
                : 'Search brokers for your prop firm...'
              }
              showFees={false}
              showAssetIndicators={true}
              showCustomBroker={true}
            />

            {/* Validation Status */}
            {isFormValid() && (
              <div className="flex items-center gap-2 text-success text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-success"></div>
                Broker selected! Ready to continue.
              </div>
            )}

            {/* Validation Error */}
            {!isFormValid() && accountData.selectedBrokerId === '' && (
              <Alert
                color="warning"
                variant="bordered"
                description="Please select a broker to continue with your setup."
              />
            )}
          </div>
        </div>
      </CardBody>

      <CardFooter className="shrink-0 border-t border-default-200">
        <Accordion variant="light" fullWidth isCompact>
          <AccordionItem
            textValue="Why do we need this?"
            key="why-needed"
            title={
              <div className="flex items-center gap-2">
                <Info size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">
                  Why do we need this?
                </span>
              </div>
            }
          >
            <ul className="text-sm text-text-secondary space-y-1 pb-2">
              <li>• Your broker determines available trading platforms and tools</li>
              <li>• Different brokers have different fee structures and minimums</li>
              <li>• Some brokers specialize in specific asset classes or account types</li>
              <li>• This helps us provide accurate cost calculations and recommendations</li>
              <li>• You can always change your broker later in Settings</li>
            </ul>
          </AccordionItem>
        </Accordion>
      </CardFooter>
    </Card>
  );
};